import { type MemberPayment, uuidSchema } from '@repo/api-contracts';
import { ArrowUpRight, Check, Loader2 } from 'lucide-react';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { usePaymentStatusQuery } from '@/entities/payment';
import {
  isConfirmReplyLost,
  paymentApiErrorMessage,
  useConfirmPayment,
} from '@/features/payment-checkout';
import { HOME_PATH, myOrderDetailPath } from '@/shared/config';
import { formatKrw, safeHttpsUrl } from '@/shared/lib';
import { Button, buttonVariants, cn, EyebrowLabel, SitePageShell } from '@/shared/ui';
import { SiteFooter } from '@/widgets/site-footer';
import { SiteHeader } from '@/widgets/site-header';

type ConfirmView =
  | { readonly step: 'confirming' }
  | { readonly step: 'done'; readonly payment: MemberPayment }
  | { readonly step: 'checking'; readonly paymentId: string }
  | { readonly step: 'error'; readonly message: string };

const FAILED_STATUSES: readonly MemberPayment['status'][] = ['ABORTED', 'EXPIRED'];

/** 완료 화면 두 칸(lg) — 왼쪽은 완료 표시·문구, 오른쪽은 결제 내용 줄과 버튼. */
const DONE_GRID_CLASS =
  'grid gap-10 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] lg:gap-12 xl:gap-16';
/** 완료가 아닌 결과(승인 중·확인 중·쿼리 오류·승인 실패)는 결제 실패 화면과 같은 한 칸 — 읽기 폭 칸에 안내, 바로 아래 버튼 줄. */
const RESULT_BLOCK_CLASS = 'flex max-w-2xl flex-col gap-6';
/** 진행 상태 안내(승인 중·확인 중) — 주문하기의 별첨 고지와 같은 중립 칸. */
const NOTICE_CLASS = 'border-l-2 border-foreground bg-muted/60 px-5 py-4';
const WARNING_CLASS =
  'border-l-2 border-destructive bg-destructive/5 px-5 py-4 text-sm font-semibold text-destructive';
/** 버튼 줄 — 휴대폰에서는 전폭으로 쌓이고, 넓은 화면(sm)에서는 한 줄에 내용 폭(최소 12rem)으로 놓인다. */
const ACTIONS_CLASS = 'flex flex-col gap-3 sm:flex-row';
/** 버튼 줄의 보조 버튼(홈으로·다시 확인) — 56px, 넓은 화면에서 내용 폭. */
const OUTLINE_ACTION_CLASS = 'h-14 w-full border-foreground/20 text-base sm:w-auto sm:min-w-48';
const HOME_LINK_CLASS = cn(
  buttonVariants({ variant: 'outline', size: 'lg' }),
  OUTLINE_ACTION_CLASS,
);

/**
 * 결제 성공 페이지 — 토스가 넘긴 paymentKey·orderId·amount 로 승인을 부른다(결제 설계 §4-3).
 * 화면 전환은 뮤테이션 훅 상태가 아니라 promise 로 구동한다 — StrictMode 재마운트가 진행 중
 * 뮤테이션에서 옵저버를 분리해 훅 상태가 영영 갱신되지 않는다(pages/verify-email 과 같은 이유).
 * 다시 확인의 결과 중 READY·조회 실패는 화면 단계를 늘리지 않고 조회 상태(statusQuery)에서 파생해
 * 확인 중 화면에 덧붙여 보여 준다.
 * 완료 화면은 두 칸(lg) — 왼쪽은 라임 표시와 큰 완료 문구, 오른쪽은 결제 내용 줄과 버튼(내 주문 보기·영수증 보기).
 * 완료가 아닌 화면(승인 중·확인 중·쿼리 오류·승인 실패)은 결제 실패 화면과 같은 한 칸이다 — 읽기 폭 칸에 안내(진행은
 * 중립 칸, 실패는 경고 칸)를 두고 바로 아래 버튼 줄(다시 확인·홈으로)을 둔다.
 * 승인 실패 문구는 서버가 답한 안내만 그대로 쓰고 통신 오류 원문은 우리 문구로 바꾼다(paymentApiErrorMessage).
 */
export const PaymentSuccessPage = (): ReactElement => {
  const [params] = useSearchParams();
  const paymentKey = params.get('paymentKey');
  const orderId = params.get('orderId');
  const amount = Number(params.get('amount'));
  const isValidAmount = Number.isSafeInteger(amount) && amount > 0;
  const { mutateAsync: confirm } = useConfirmPayment();
  const [view, setView] = useState<ConfirmView>({ step: 'confirming' });
  const statusQuery = usePaymentStatusQuery(view.step === 'checking' ? view.paymentId : '', {
    enabled: false,
  });
  // StrictMode 이중 마운트에서 같은 paymentKey 로 두 번 승인 요청하지 않게 한 번만 부른다.
  const firedKey = useRef<string | null>(null);

  useEffect(() => {
    if (
      paymentKey === null ||
      orderId === null ||
      !isValidAmount ||
      firedKey.current === paymentKey
    ) {
      return;
    }

    firedKey.current = paymentKey;

    confirm({ paymentKey, orderId, amount })
      .then((result) => {
        setView(
          result.outcome === 'DONE'
            ? { step: 'done', payment: result.payment }
            : { step: 'checking', paymentId: result.paymentId },
        );
      })
      .catch((error: unknown) => {
        // 응답을 못 받았으면 승인됐을 수 있다 — 토스가 준 orderId(= payments.id)로 확인 중 화면에 간다.
        setView(
          isConfirmReplyLost(error)
            ? { step: 'checking', paymentId: orderId }
            : {
                step: 'error',
                message: paymentApiErrorMessage(error, '결제를 승인하지 못했습니다.'),
              },
        );
      });
  }, [paymentKey, orderId, amount, isValidAmount, confirm]);

  const handleRecheck = async (): Promise<void> => {
    const result = await statusQuery.refetch();
    const payment = result.data;

    if (!payment || payment.status === 'READY') {
      return;
    }

    setView(
      FAILED_STATUSES.includes(payment.status)
        ? { step: 'error', message: '결제가 승인되지 않았습니다.' }
        : { step: 'done', payment },
    );
  };

  // 조회가 실패했으면 이전 조회의 READY(낡은 데이터)로 안내하지 않고 오류만 보여 준다.
  const isStillReady = !statusQuery.isError && statusQuery.data?.status === 'READY';

  let body: ReactElement;

  if (paymentKey === null || orderId === null || !isValidAmount) {
    body = (
      <div className={RESULT_BLOCK_CLASS}>
        <p role="alert" className={WARNING_CLASS}>
          결제 결과 정보가 올바르지 않습니다.
        </p>
        <div className={ACTIONS_CLASS}>
          <Link to={HOME_PATH} className={HOME_LINK_CLASS}>
            홈으로
          </Link>
        </div>
      </div>
    );
  } else if (view.step === 'done') {
    // 영수증은 https 주소만 연다 — 내 주문 상세·내 견적 상세와 같은 규칙. 승인 직후라 상태 조건은 없다.
    const receiptUrl = safeHttpsUrl(view.payment.receiptUrl);
    body = (
      <div className={DONE_GRID_CLASS}>
        <div className="flex flex-col gap-5">
          <span
            aria-hidden
            className="flex size-12 items-center justify-center bg-brand-lime text-black"
          >
            <Check className="size-6" strokeWidth={2} />
          </span>
          <h2 className="text-[clamp(1.75rem,3vw,2.5rem)] leading-tight font-bold tracking-[-0.02em] text-balance">
            결제가 완료되었습니다
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            주문 진행 상황은 내 주문에서 확인할 수 있습니다.
          </p>
        </div>

        <div className="flex flex-col gap-6">
          <dl className="divide-y divide-foreground/10 border-y border-foreground/10 text-sm wrap-anywhere">
            <div className="flex gap-4 py-3">
              <dt className="w-24 shrink-0 text-muted-foreground sm:w-28">주문</dt>
              <dd className="min-w-0 flex-1 font-medium">{view.payment.orderName}</dd>
            </div>
            <div className="flex gap-4 py-3">
              <dt className="w-24 shrink-0 text-muted-foreground sm:w-28">금액</dt>
              <dd className="min-w-0 flex-1 font-semibold tabular-nums">
                {formatKrw(view.payment.amount)}
              </dd>
            </div>
            <div className="flex gap-4 py-3">
              <dt className="w-24 shrink-0 text-muted-foreground sm:w-28">결제 수단</dt>
              <dd className="min-w-0 flex-1">{view.payment.method ?? '-'}</dd>
            </div>
          </dl>
          <div className="flex flex-col gap-3">
            {/* 링크는 결제의 주문 ID(memberPayment.orderId)로 만든다 — 쿼리의 orderId 는 결제 ID 다(R27). */}
            {uuidSchema.safeParse(view.payment.orderId).success ? (
              <Link
                to={myOrderDetailPath(view.payment.orderId)}
                className={cn(buttonVariants({ size: 'lg' }), 'h-14 w-full text-base')}
              >
                내 주문 보기
              </Link>
            ) : null}
            {receiptUrl !== null ? (
              <a
                href={receiptUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  buttonVariants({ variant: 'outline', size: 'lg' }),
                  'h-12 w-full border-foreground/20',
                )}
              >
                영수증 보기
                <ArrowUpRight aria-hidden strokeWidth={1.5} />
              </a>
            ) : null}
          </div>
        </div>
      </div>
    );
  } else if (view.step === 'checking') {
    body = (
      <div className={RESULT_BLOCK_CLASS}>
        <div role="status" className={cn(NOTICE_CLASS, 'flex flex-col gap-1.5 text-sm')}>
          <p className="text-base font-semibold text-balance">
            결제를 확인하고 있습니다. 잠시 후 다시 확인해 주세요.
          </p>
          {isStillReady ? (
            <p className="text-balance text-muted-foreground">
              아직 결제 결과를 받지 못했습니다. 잠시 후 다시 확인해 주세요.
            </p>
          ) : null}
        </div>
        {statusQuery.isError ? (
          <p role="alert" className={WARNING_CLASS}>
            결제 상태를 확인하지 못했습니다. 잠시 후 다시 시도해 주세요.
          </p>
        ) : null}
        <div className={ACTIONS_CLASS}>
          <Button
            variant="outline"
            size="lg"
            className={OUTLINE_ACTION_CLASS}
            isLoading={statusQuery.isFetching}
            onClick={() => {
              void handleRecheck();
            }}
          >
            다시 확인
          </Button>
        </div>
      </div>
    );
  } else if (view.step === 'error') {
    body = (
      <div className={RESULT_BLOCK_CLASS}>
        <p role="alert" className={WARNING_CLASS}>
          {view.message}
        </p>
        <div className={ACTIONS_CLASS}>
          <Link to={HOME_PATH} className={HOME_LINK_CLASS}>
            홈으로
          </Link>
        </div>
      </div>
    );
  } else {
    body = (
      <div className={RESULT_BLOCK_CLASS}>
        <div className={cn(NOTICE_CLASS, 'flex items-center gap-3')}>
          <Loader2 aria-hidden className="size-5 shrink-0 animate-spin text-muted-foreground" />
          <p role="status" className="text-sm text-balance">
            결제를 승인하고 있습니다. 잠시만 기다려 주세요.
          </p>
        </div>
      </div>
    );
  }

  return (
    <SitePageShell header={<SiteHeader />} footer={<SiteFooter />}>
      <div className="flex flex-col gap-10 sm:gap-12">
        <header className="flex flex-col gap-4 border-b border-foreground/10 pb-8">
          <EyebrowLabel>Payment</EyebrowLabel>
          <h1 className="text-[clamp(2.25rem,4.5vw,4rem)] leading-none font-bold tracking-[-0.03em]">
            결제 결과
          </h1>
        </header>
        {body}
      </div>
    </SitePageShell>
  );
};
